// services/packs.js — разфасовката на реда в доставката (Макс, 27.10.2026).
//
// Материалите на ресторанта се водят в кг/л (рецептата пише грамове), а
// доставчикът продава „1 бр." — и то в РАЗЛИЧНИ разфасовки: кисело мляко от
// 400 г до 2 кг, олио 1 л или 5 л. Едно преобразуване „1 бр. = X" на
// артикула не стига, затова разфасовката е на РЕДА: line.pack = колко базови
// единици (кг/л) е 1 единица от реда. Попълва се сама — от описанието
// („Кисело мляко 3,6% 400г" → 0,4), от предишна доставка от същия доставчик
// със същото описание, или от единственото преобразуване на артикула — и
// човекът я поправя на екрана на доставката.
//
// Чисти функции, без база. Екранът има огледално копие на parsePack
// (src/shared/packs.js), сверявано с тест.

const articlesSvc = require('./articles');

// Мерките с тегло/обем: в тях складът брои грамажа. Ключовете са каноничните
// от articles.unitKey.
const METRIC_BASE = { кг: 'mass', г: 'mass', л: 'volume', мл: 'volume' };
const TO_BASE = { г: 0.001, кг: 1, мл: 0.001, л: 1 };

function isMetric(measure) {
  return !!METRIC_BASE[articlesSvc.unitKey(measure)];
}

// Мярката на реда е тегло/обем, съвместим(о) с базата → преобразува се
// стандартно (г → кг), без разфасовка.
function metricUnit(article, unit) {
  const b = METRIC_BASE[articlesSvc.unitKey(article && article.measure)];
  const u = METRIC_BASE[articlesSvc.unitKey(unit)];
  return !!b && b === u;
}

// Грамажът в описанието: „400г", „0,5 кг", „2кг", „1 л", „500 мл", „5L",
// „x400gr". Взима се ПОСЛЕДНОТО число с мярка (в „Олио 1л х 6" е 1 л;
// процентите „3,6%" не са мярка). Връща колко базови единици (кг или л) е
// 1 брой, или null — само когато мярката на текста е от същия вид като
// базата (грамове при кг, милилитри при л).
const PACK_RE = /(\d+(?:[.,]\d+)?)\s*(кг|kg|гр|г|gr|g|мл|ml|л|l|литра|литър)(?![a-zа-я])/gi;
function parsePack(text, baseMeasure) {
  const kind = METRIC_BASE[articlesSvc.unitKey(baseMeasure)];
  if (!kind) return null;
  const s = String(text || '').toLowerCase();
  let found = null;
  let m;
  PACK_RE.lastIndex = 0;
  while ((m = PACK_RE.exec(s)) !== null) {
    const raw = m[2].toLowerCase();
    const unit = { kg: 'кг', гр: 'г', gr: 'г', g: 'г', ml: 'мл', l: 'л', литра: 'л', литър: 'л' }[raw] || raw;
    if (METRIC_BASE[unit] !== kind) continue;
    const n = Number(m[1].replace(',', '.'));
    if (!(n > 0)) continue;
    const baseKey = articlesSvc.unitKey(baseMeasure);
    // В база г/мл (рядко) — броим в нея; иначе в кг/л.
    const inBase = baseKey === 'г' || baseKey === 'мл' ? n * TO_BASE[unit] * 1000 : n * TO_BASE[unit];
    found = Math.round(inBase * 1e6) / 1e6;
  }
  return found;
}

// Нужна ли е разфасовка за реда: артикулът се води в кг/л, а редът е в
// бройки (или друга мярка, която не е тегло/обем и няма преобразуване).
function needsPack(article, unit) {
  if (!article || !isMetric(article.measure)) return false;
  if (metricUnit(article, unit)) return false;
  const u = articlesSvc.unitKey(unit);
  if (!u || u === articlesSvc.unitKey(article.measure)) return false;
  return true;
}

// Количеството на реда в базовата мярка на склада. Редът на правилата:
// 1) мярката е тегло/обем → стандартно (2 кг, 400 г);
// 2) разфасовка на реда → бройки × разфасовка (печели пред преобразуването
//    на артикула — то е едно, а разфасовките са различни);
// 3) преобразуване на артикула (кашон = 24 бр.);
// иначе грешка, която казва какво да се попълни.
function lineBase(article, qty, unit, pack) {
  const q = Math.abs(Number(qty) || 0);
  if (metricUnit(article, unit)) return articlesSvc.toBase(article, q, unit);
  const p = Number(pack);
  if (p > 0 && needsPack(article, unit)) return Math.round(q * p * 1e6) / 1e6;
  try {
    return articlesSvc.toBase(article, q, unit);
  } catch (e) {
    if (needsPack(article, unit)) {
      throw new Error('колко ' + article.measure + ' е 1 ' + (String(unit || '').trim() || 'бр.') + ' „' + (article.name || '') + '"? Попълнете разфасовката на реда.');
    }
    throw e;
  }
}

const descKey = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 200);

// Паметта: последната разфасовка за същия доставчик и същото описание (или
// същия код на доставчика) от вече записаните доставки на фирмата.
function memoryFrom(docs, contactId) {
  const mem = new Map();
  const sorted = (Array.isArray(docs) ? docs : [])
    .filter((d) => contactId != null && Number(d.contact_id) === Number(contactId))
    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')) || (Number(a.id) || 0) - (Number(b.id) || 0));
  for (const d of sorted) {
    for (const l of Array.isArray(d.lines) ? d.lines : []) {
      if (!(Number(l.pack) > 0) || l.article_id == null) continue;
      const v = { pack: Number(l.pack), article_id: Number(l.article_id) };
      if (l.supplier_code) mem.set('c:' + l.supplier_code, v);
      if (l.description) mem.set('d:' + descKey(l.description), v);
    }
  }
  return mem;
}

// Попълва липсващата разфасовка на редовете. Ръчно въведената не се пипа.
// arts — Map(id → артикул); mem — от memoryFrom.
function fillPacks(lines, arts, mem) {
  return (Array.isArray(lines) ? lines : []).map((l) => {
    if (!l || l.article_id == null) return l;
    const a = arts.get(Number(l.article_id));
    if (!needsPack(a, l.unit)) {
      if (l.pack != null) { const c = Object.assign({}, l); delete c.pack; return c; }
      return l;
    }
    if (Number(l.pack) > 0) return l;
    const hit = mem && ((l.supplier_code && mem.get('c:' + l.supplier_code)) || mem.get('d:' + descKey(l.description)));
    let pack = hit && hit.article_id === Number(a.id) ? hit.pack : null;
    if (!pack) pack = parsePack(l.description, a.measure);
    if (!pack) {
      // Единственото преобразуване за тази мярка — то е и без това
      // правилото на артикула; пишем го, за да се вижда на реда.
      const conv = (Array.isArray(a.conversions) ? a.conversions : []).find((c) => articlesSvc.unitKey(c.unit) === articlesSvc.unitKey(l.unit));
      if (conv && Number(conv.factor) > 0) pack = Number(conv.factor);
    }
    return pack ? Object.assign({}, l, { pack }) : l;
  });
}

module.exports = { isMetric, metricUnit, parsePack, needsPack, lineBase, memoryFrom, fillPacks, descKey };
